/**
 * NCP-4847: guard against XSS / open-redirect (CWE-601) when a
 * backend-supplied URL is assigned to `window.location.href` /
 * `newTab.location.href` or handed to `window.open`.
 *
 * Document download links are presigned S3 URLs (or fresh server-generated
 * links) returned by the authenticated payment backend, so the host is not
 * fixed. We therefore validate the *scheme* rather than an exhaustive host
 * allowlist: only `http:`/`https:` absolute URLs (or same-origin relative
 * paths) are permitted, which conclusively rejects `javascript:`, `data:`,
 * `vbscript:` and `file:` payloads — the vectors that would let an attacker
 * run script or exfiltrate via a location assignment.
 */
export const sanitizeRedirectUrl = (url: unknown): string | null => {
  if (typeof url !== 'string') {
    return null;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  // Distinguish absolute URLs (parse with no base) from relative paths (which
  // throw without a base and are resolved against the current origin).
  let parsed: URL;
  let isAbsolute = true;
  try {
    parsed = new URL(trimmed);
  } catch {
    isAbsolute = false;
    try {
      parsed = new URL(
        trimmed,
        typeof window !== 'undefined' ? window.location.origin : undefined
      );
    } catch {
      return null;
    }
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return null;
  }

  // The security property here is the scheme allowlist above, which rejects
  // javascript:/data:/vbscript:/file: payloads. For an already-absolute URL we
  // return the caller's exact string rather than the URL-normalized form so a
  // presigned S3 link (whose signature covers its precise encoding) is never
  // silently re-encoded; relative paths are returned resolved to absolute.
  return isAbsolute ? trimmed : parsed.href;
};

/**
 * Assign a validated URL to a target's `location.href`, or open it in a new
 * tab. Unsafe URLs are ignored (no navigation) rather than assigned.
 */
export const safeAssignLocation = (target: Window, url: unknown): boolean => {
  const safe = sanitizeRedirectUrl(url);
  if (!safe) {
    return false;
  }
  target.location.href = safe;
  return true;
};

/**
 * Safely open a validated URL in a new tab. No-ops on unsafe URLs.
 *
 * NCP-5009/NCP-5015: opened with `noopener,noreferrer` so the new document
 * cannot reach back through `window.opener` and navigate the payment portal tab
 * to a page of its choosing (reverse tabnabbing, CWE-1022). This matches the
 * equivalent helper in foxden-admin-portal.
 *
 * Returns whether a tab was opened rather than the `Window` handle: `noopener`
 * makes `window.open` return `null` by design, so a `Window | null` return
 * could only ever be `null` and would mislead callers into treating a
 * successful open as a failure.
 */
export const safeOpenUrl = (url: unknown): boolean => {
  const safe = sanitizeRedirectUrl(url);
  if (!safe) {
    return false;
  }
  window.open(safe, '_blank', 'noopener,noreferrer');
  return true;
};
